/**
 * PRÉ-AUDIT DÉTAILLÉ - Nouveaux Restaurants
 * La Fontaine Gaillon & Hollywood Savoy
 * Rapports professionnels pour séduction prospects
 */

export interface PreAuditReport {
  restaurantId: string;
  restaurantName: string;
  auditDate: string;
  
  // Scores globaux
  overallScore: number;
  healthStatus: 'critical' | 'warning' | 'good';
  urgencyLevel: 'high' | 'medium' | 'low';
  
  // Résumé exécutif
  executiveSummary: {
    situation: string;
    mainChallenges: string[];
    quickWins: string[];
    estimatedLostRevenue: string;
    potentialGain: string;
  };
  
  // Scores par catégorie
  categoryScores: {
    category: string;
    score: number;
    status: 'critical' | 'warning' | 'good';
    p0Count: number;
    p1Count: number;
    p2Count: number;
    icon: string;
  }[];
  
  // Métriques actuelles
  currentMetrics: {
    seo: {
      organicTraffic: number;
      positionAverage: number;
      visibilityScore: number;
      domainAuthority: number;
      backlinks: number;
      keywords: number;
    };
    geo: {
      googleRating: number;
      reviewCount: number;
      citations: number;
      localPackPresence: string;
      gmbCompleteness: number;
    };
    reputation: {
      theForkRating: number;
      serviceScore: number;
      ambianceScore: number;
      foodScore: number;
    };
  };
  
  // Axes stratégiques d'amélioration
  strategicAxes: {
    id: string;
    title: string;
    description: string;
    currentSituation: string;
    targetObjective: string;
    priority: 'P0' | 'P1' | 'P2';
    estimatedImpact: string;
    timeline: string;
    budget: string;
    actions: {
      title: string;
      description: string;
      isVisible: boolean; // Pour masquer certaines actions (teasing)
    }[];
    kpis: {
      name: string;
      current: string;
      target: string;
      improvement: string;
    }[];
  }[];
  
  // Plan 6 mois
  sixMonthPlan: {
    month: number;
    focus: string;
    keyActions: string[];
    expectedResults: string[];
    investment: string;
  }[];
  
  // Recommandations pack
  recommendedPack: {
    name: string;
    price: string;
    duration: string;
    roi: string;
    included: string[];
  };
}

// ============================================================================
// PRÉ-AUDIT LA FONTAINE GAILLON
// ============================================================================

export const preAuditFontaineGaillon: PreAuditReport = {
  restaurantId: 'fontaine-gaillon',
  restaurantName: 'La Fontaine Gaillon',
  auditDate: new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }),
  
  overallScore: 42,
  healthStatus: 'warning',
  urgencyLevel: 'medium',
  
    executiveSummary: {
    situation: 'Situation Paradoxale : Vous disposez d\'actifs exceptionnels (terrasse neoclassique unique, 6 salons historiques, e-reputation 4.5 etoiles) MAIS votre infrastructure numerique bride considerablement votre potentiel. Votre visibilite digitale ne reflete pas l\'excellence de votre etablissement.',
    mainChallenges: [
      'CRITIQUE - Pas de site internet dedie : Partage fitz-group.fr avec 4 autres etablissements. Les moteurs de recherche ne comprennent pas votre positionnement unique.',
      'URGENT - Invisible pour ChatGPT et les IA : Vos menus sont en PDF, aucune donnee structuree. 40% des recherches 2025 passent par IA - vous etes absent de leurs recommandations.',
      'BLOQUANT - Aucun chat IA 24/7 : Les visiteurs hors horaires (60% du trafic) partent sans reponse. Vos concurrents repondent instantanement.',
      'MANQUE A GAGNER - Salons prives invisibles : 6 salons prestigieux sans pages dedie es. Potentiel B2B 50-200k EUR/an non capte.',
      'OPPORTUNITE RATEE - Offres groupes mal visibles : Informations privatisation enfouies. Concurrents captent ce marche lucratif.'
    ],
    quickWins: [
      '✅ Digitaliser les menus (PDF → HTML + Schema.org) : 3 semaines',
      '✅ Créer landing terrasse saisonnière : 2 semaines, +15k€ CA/mois avril-sept',
      '✅ Pages individuelles 6 salons privés : 4 semaines, potentiel 50-200k€/an B2B',
      '✅ Correction acoustique (musique trop forte détectée) : 1 semaine, protège note 4.5★'
    ],
    estimatedLostRevenue: '120k€ - 180k€/an',
    potentialGain: '+90k€ - 250k€/an avec stratégie complète 12 mois'
  },
  
  categoryScores: [
    {
      category: 'SEO Technique',
      score: 35,
      status: 'critical',
      p0Count: 3,
      p1Count: 1,
      p2Count: 0,
      icon: 'Shield'
    },
    {
      category: 'GEO & Moteurs Génératifs',
      score: 20,
      status: 'critical',
      p0Count: 3,
      p1Count: 0,
      p2Count: 0,
      icon: 'Globe'
    },
    {
      category: 'Contenu & Sémantique',
      score: 45,
      status: 'warning',
      p0Count: 1,
      p1Count: 3,
      p2Count: 0,
      icon: 'FileText'
    },
    {
      category: 'E-réputation & Local',
      score: 65,
      status: 'good',
      p0Count: 1,
      p1Count: 1,
      p2Count: 1,
      icon: 'Award'
    }
  ],
  
  currentMetrics: {
    seo: {
      organicTraffic: 85,
      positionAverage: 18.5,
      visibilityScore: 62,
      domainAuthority: 35,
      backlinks: 450,
      keywords: 120
    },
    geo: {
      googleRating: 4.5,
      reviewCount: 1250,
      citations: 78,
      localPackPresence: '5/120 (4%)',
      gmbCompleteness: 72
    },
    reputation: {
      theForkRating: 8.8,
      serviceScore: 8.9,
      ambianceScore: 8.5,
      foodScore: 8.8
    }
  },
  
  strategicAxes: [
    {
      id: 'axe_fg_1',
      title: '🚨 URGENT : Migration Domaine Dédié & Clarification Autorité',
      description: 'Votre site est hébergé en sous-répertoire fitz-group.fr, ce qui dilue votre autorité SEO entre 5+ concepts différents. Google ne comprend pas clairement votre positionnement.',
      currentSituation: 'Site en fitz-group.fr/fontaine-gaillon-paris - Autorité thématique fragmentée entre Gaillon, Savoy, Fitzgerald, Abstinence... Topic Clarity: 35%',
      targetObjective: 'Domaine propre lafontainegaillon.com - 100% autorité dédiée - Topic Clarity: 90%+',
      priority: 'P0',
      estimatedImpact: '+20-30 positions moyennes sur requêtes génériques + amélioration autorité SEO',
      timeline: '2-3 mois migration + consolidation',
      budget: '8,000€ (acquisition 500€ + migration 5k€ + SEO 2.5k€)',
      actions: [
        {
          title: 'Acquisition domaine lafontainegaillon.com',
          description: 'Disponible. Négociation et acquisition immédiate.',
          isVisible: true
        },
        {
          title: 'Migration technique avec redirections 301',
          description: 'Transfert complet avec preservation du SEO existant.',
          isVisible: true
        },
        {
          title: 'Nettoyage architecture : suppression navigation inter-sites',
          description: 'Éliminer confusion avec autres établissements Fitz Group.',
          isVisible: true
        },
        {
          title: '[RECOMMANDATION COMPLÈTE MASQUÉE]',
          description: 'Stratégie Topic Cluster avancée et optimisation...  → Détails dans accompagnement STRATOS',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Domain Authority propre', current: '35 (partagé)', target: '45+', improvement: '+29%' },
        { name: 'Trafic organique', current: '85/mois', target: '250+/mois', improvement: '+194%' },
        { name: 'Topic Clarity Score', current: '35%', target: '90%', improvement: '+157%' }
      ]
    },
    
    {
      id: 'axe_fg_2',
      title: '🤖 CRITIQUE : Invisibilité Moteurs Génératifs (ChatGPT, Google SGE)',
      description: 'Vos menus sont en PDF et aucune donnée structurée n\'est présente. Les IA ne peuvent pas "lire" votre site. 40% des recherches 2025 passent par IA - vous êtes absent de leurs recommandations.',
      currentSituation: 'Menus PDF + 0 Schema.org = Invisible pour IA. Taux recommandation: 10%',
      targetObjective: 'Menus HTML structurés + Schema.org complet = Recommandé par 80% des IA',
      priority: 'P0',
      estimatedImpact: '20-35% du trafic futur proviendra recommandations IA',
      timeline: '1-2 mois implémentation + 6 mois impact complet',
      budget: '5,000€ (digitalisation menus + Schema.org + tests)',
      actions: [
        {
          title: 'Conversion menus PDF → HTML avec balisage complet',
          description: 'Chaque plat devient une entité indexable par les IA.',
          isVisible: true
        },
        {
          title: 'Implémentation Schema.org Restaurant + Menu + MenuItem',
          description: 'Homard 48€, Turbot 52€, Soufflé 18€ structurés pour extraction.',
          isVisible: true
        },
        {
          title: 'Balisage salons privés (Event/PrivateRoom schema)',
          description: 'Mansart, Krug, Rémy Martin... indexables individuellement.',
          isVisible: true
        },
        {
          title: '[STRATÉGIE GEO AVANCÉE MASQUÉE]',
          description: 'Entity-Based SEO, FAQ structurée 50+ questions, optimisation featured snippets... → Accompagnement STRATOS',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Schema.org implémenté', current: 'Non', target: 'Complet', improvement: 'CRITIQUE' },
        { name: 'Recommandations IA', current: '10%', target: '80%', improvement: '+700%' },
        { name: 'Featured Snippets', current: '0', target: '8+', improvement: 'Nouveau canal' }
      ]
    },
    
    {
      id: 'axe_fg_3',
      title: '💼 Potentiel B2B Inexploité : 6 Salons Historiques Sans Visibilité',
      description: 'Vous possédez 6 salons privés prestigieux (Mansart, Krug, Rémy Martin...) mais aucune page dédiée. 0 positionnement sur "privatisation restaurant Paris". Concurrent Drouant capte ce marché.',
      currentSituation: '0 mot-clé "privatisation" - Demandes devis: ~1/mois - CA privatisation: quasi inexistant',
      targetObjective: '35+ keywords B2B - 15 demandes devis/mois - 50-200k€ CA privatisation/an',
      priority: 'P0',
      estimatedImpact: '2-5 privatisations/mois = 50-200k€ CA additionnel/an',
      timeline: '2-4 mois positionnement offre',
      budget: '9,500€ (pages salons 4.5k€ + LinkedIn Ads 4.5k€ + brochure 500€)',
      actions: [
        {
          title: 'Création 6 pages individuelles par salon',
          description: '/salon-mansart, /salon-krug... avec photos, capacité, équipements, pricing transparent.',
          isVisible: true
        },
        {
          title: 'Page pilier /privatisation FR + EN',
          description: 'Landing optimisée SEO corporate : "privatisation restaurant paris opéra".',
          isVisible: true
        },
        {
          title: 'Galerie photos événements corporate + testimonials',
          description: 'Proof of concept - Case studies entreprises (avec accord).',
          isVisible: true
        },
        {
          title: '[STRATÉGIE ACQUISITION B2B COMPLÈTE MASQUÉE]',
          description: 'LinkedIn Ads ciblage, partenariats agences, workflow lead nurturing, programme commission... → Accompagnement STRATOS Premium',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Demandes devis B2B', current: '~1/mois', target: '15+/mois', improvement: 'x15' },
        { name: 'CA privatisations', current: '<5k€/an', target: '50-200k€/an', improvement: 'x10-40' },
        { name: 'Trafic /privatisation', current: '0%', target: '8%', improvement: 'Nouveau segment' }
      ]
    },
    
    {
      id: 'axe_fg_4',
      title: '🌿 Exploitation Actif Terrasse - SEO Saisonnier',
      description: 'Votre terrasse néoclassique avec statue de triton est unique à Paris (avis: "out of this world"). C\'est votre USP principal MAIS sous-exploité en SEO saisonnier.',
      currentSituation: 'Position 8 "terrasse paris 2" - Pas de landing dédiée - Trafic saisonnier +15% seulement',
      targetObjective: 'Position 3 keywords terrasse - Landing optimisée - Trafic saisonnier +80%',
      priority: 'P1',
      estimatedImpact: '+100-150 couverts avril-septembre = +8-12k€ CA/mois saison haute',
      timeline: '2 mois (avant saison mars)',
      budget: '4,500€ (photos 2k€ + landing 1.5k€ + ads 1k€)',
      actions: [
        {
          title: 'Landing /terrasse-neoclassique FR + EN',
          description: 'Optimisée "restaurant terrasse paris opéra", "jardin secret paris".',
          isVisible: true
        },
        {
          title: 'Shooting photo terrasse 4 saisons',
          description: 'Printemps, été, automne (chauffage), hiver (couvertures). Montrer confort année.',
          isVisible: true
        },
        {
          title: 'Upload 30+ photos terrasse GMB + posts réguliers mars-sept',
          description: 'Visibilité Google Maps terrasse.',
          isVisible: true
        },
        {
          title: '[STRATÉGIE SAISONNIÈRE AVANCÉE]',
          description: 'Google Ads saisonniers, monitoring météo, offres dynamiques, partenariats influenceurs... → STRATOS Premium',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Position "terrasse paris 2"', current: '8', target: '3', improvement: '-5 positions' },
        { name: 'Réservations terrasse', current: '60% saison', target: '85% saison', improvement: '+42%' },
        { name: 'CA terrasse avril-sept', current: 'Non suivi', target: '+90k€/saison', improvement: 'Nouveau tracking' }
      ]
    },
    
    {
      id: 'axe_fg_5',
      title: '🌍 Ouverture Clientèle Internationale',
      description: 'Paris = 15M touristes/an. Votre trafic international est de seulement 30% alors que le potentiel est de 60%+. Aucune balise hreflang, contenu EN sous-optimisé.',
      currentSituation: 'Trafic international: 30% - 0 backlink média international - Versions linguistiques mal configurées',
      targetObjective: 'Trafic international: 55% - 25 backlinks premium - 4 langues optimisées (FR/EN/ES/PT)',
      priority: 'P1',
      estimatedImpact: '+180 réservations/mois touristes = 18-25k€ CA/mois',
      timeline: '3-6 mois déploiement multilingue',
      budget: '12,000€ (traductions 4k€ + hreflang 2k€ + ads international 6k€)',
      actions: [
        {
          title: 'Implémentation balises hreflang FR/EN/ES/PT',
          description: 'Signaler à Google quelle version servir selon pays utilisateur.',
          isVisible: true
        },
        {
          title: 'Optimisation version EN : contenus "rooftop dining", "romantic dinner"',
          description: 'Keywords touristiques haute valeur.',
          isVisible: true
        },
        {
          title: '[STRATÉGIE INTERNATIONALE COMPLÈTE MASQUÉE]',
          description: '12 landing pages géolocalisées, partenariats concierges 5*, campagnes multilingues... → Pack Signature STRATOS',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Trafic international', current: '30%', target: '55%', improvement: '+83%' },
        { name: 'Réservations touristes', current: '~40/mois', target: '220/mois', improvement: 'x5.5' }
      ]
    },
    
    {
      id: 'axe_fg_6',
      title: '🤝 Service Client 24/7 & Canaux Réservation',
      description: 'Aucun chatbot multilingue. Visiteurs hors horaires (60%) = perdus. Absence TheFork/OpenTable = canaux de réservation limités.',
      currentSituation: 'Temps réponse: 4-24h - Disponibilité: 0% hors horaires - Plateformes: 1 seule',
      targetObjective: 'Chatbot IA 24/7 - Réponse <30s - 5 plateformes réservation actives',
      priority: 'P1',
      estimatedImpact: '+30-50 réservations/mois = 3-5k€ CA/mois',
      timeline: '1-2 mois déploiement',
      budget: 'Inclus Pack Premium',
      actions: [
        {
          title: 'Chatbot IA multilingue (FR/EN/ES/PT)',
          description: 'Réponses instantanées FAQ, menu, privatisation, réservation directe.',
          isVisible: true
        },
        {
          title: 'Inscription TheFork + OpenTable',
          description: 'Capter clientèle EU (TheFork) + US (OpenTable).',
          isVisible: true
        },
        {
          title: '[SYSTÈME MULTI-CANAL AVANCÉ]',
          description: 'Dashboard centralisé, Google Reserve, analytics attribution, remarketing... → Pack Premium',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Disponibilité support', current: '0% (hors horaires)', target: '24/7', improvement: 'Infini' },
        { name: 'Taux conversion site', current: '~2%', target: '4-5%', improvement: '+100-150%' }
      ]
    },
    
    {
      id: 'axe_fg_7',
      title: '🔧 Corrections Techniques Fondamentales',
      description: 'Infrastructure technique présentant des failles qui pénalisent votre référencement. Problème acoustique (musique trop forte) détecté dans avis récents - risque pour votre excellente note 4.5★.',
      currentSituation: 'Avis négatifs "musique forte" (oct 2024) - Gestion TheFork défaillante (YUMS non remboursés)',
      targetObjective: '0 avis négatifs acoustique - Formation équipe TheFork parfaite - Protection note 4.5★',
      priority: 'P0',
      estimatedImpact: 'Protéger e-réputation (asset majeur) + améliorer expérience client',
      timeline: '1-2 semaines corrections',
      budget: '2,500€ (audit acoustique 800€ + panneaux 1.2k€ + formation 500€)',
      actions: [
        {
          title: 'Audit acoustique professionnel salle + terrasse',
          description: 'Mesurer décibels, identifier sources bruit, recommandations traitement.',
          isVisible: true
        },
        {
          title: 'Réduction volume musique -20% + panneaux acoustiques (si requis)',
          description: 'Protéger positionnement "calme & élégance" vs Hollywood Savoy "festif".',
          isVisible: true
        },
        {
          title: 'Formation équipe TheFork + YUMS',
          description: 'Éviter futurs avis négatifs problèmes techniques réservation.',
          isVisible: true
        },
        {
          title: '[OPTIMISATIONS TECHNIQUES MASQUÉES]',
          description: 'Core Web Vitals, lazy loading, CDN, monitoring continu... → Accompagnement technique STRATOS',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Avis négatifs "bruit"', current: '2/mois', target: '0/mois', improvement: '-100%' },
        { name: 'Note Google', current: '4.5★', target: '4.6★ (protection)', improvement: '+0.1★' },
        { name: 'Satisfaction acoustique', current: 'Non mesurée', target: '95%', improvement: 'Setup tracking' }
      ]
    }
  ],
  
  sixMonthPlan: [
    {
      month: 1,
      focus: 'Fondations Techniques & Urgences',
      keyActions: [
        'Audit + correction acoustique (protéger note 4.5★)',
        'Digitalisation menus PDF → HTML + Schema.org',
        'Shooting photo terrasse (prép saison)',
        'Formation équipe TheFork'
      ],
      expectedResults: [
        '0 avis négatifs "bruit"',
        'Menus visibles moteurs génératifs',
        '30 photos terrasse uploadées GMB'
      ],
      investment: '10,500€'
    },
    {
      month: 2,
      focus: 'SEO Saisonnier & B2B',
      keyActions: [
        'Landing /terrasse online (avant mars)',
        'Création 6 pages salons privés',
        'Page pilier /privatisation',
        'Campagne Google Ads terrasse'
      ],
      expectedResults: [
        'Position 3 "terrasse paris"',
        '5 demandes devis privatisation',
        'Trafic SEO +45%'
      ],
      investment: '13,000€'
    },
    {
      month: 3,
      focus: 'Saison Haute Terrasse & International',
      keyActions: [
        'Activation campagne terrasse (mars-sept)',
        'Implémentation hreflang',
        '[5 autres actions stratégiques...]'
      ],
      expectedResults: [
        '+80 réservations terrasse',
        '[Résultats complets détaillés dans accompagnement]'
      ],
      investment: '8,500€'
    },
    {
      month: 4,
      focus: '[PLAN COMPLET MOIS 4-6 DISPONIBLE]',
      keyActions: [
        'Capitalisation momentum terrasse',
        'Optimisation internationale',
        '[Détails complets dans accompagnement STRATOS Premium]'
      ],
      expectedResults: [
        '+150 réservations/mois',
        'ROI 280% visible'
      ],
      investment: '[Budget détaillé dans proposition]'
    },
    {
      month: 5,
      focus: '[STRATÉGIE AVANCÉE]',
      keyActions: ['[Accès complet avec accompagnement STRATOS]'],
      expectedResults: ['[KPIs détaillés dans reporting mensuel]'],
      investment: '[Inclus Pack Premium]'
    },
    {
      month: 6,
      focus: 'Consolidation & Scale',
      keyActions: ['[Plan détaillé fourni lors du kick-off]'],
      expectedResults: [
        'Objectif global: +35% réservations',
        '+90-250k€ CA additionnel/an'
      ],
      investment: '[Total 6 mois: 65,000€]'
    }
  ],
  
  recommendedPack: {
    name: 'Pack Premium',
    price: '2,000€/mois',
    duration: 'Engagement 6 mois minimum',
    roi: '150-250% en 12 mois',
    included: [
      '✅ Migration domaine dédié + suivi technique',
      '✅ Digitalisation menus + Schema.org complet',
      '✅ Création 8 pages stratégiques (terrasse, salons, histoire...)',
      '✅ Campagnes Google/LinkedIn Ads gérées',
      '✅ Chatbot IA multilingue 24/7',
      '✅ Optimisation GMB + réponses avis',
      '✅ Reporting mensuel détaillé',
      '... et 12 autres services → Voir proposition complète'
    ]
  }
};

// ============================================================================
// PRÉ-AUDIT HOLLYWOOD SAVOY
// ============================================================================

export const preAuditHollywoodSavoy: PreAuditReport = {
  restaurantId: 'hollywood-savoy',
  restaurantName: 'Hollywood Savoy',
  auditDate: new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' }),
  
  overallScore: 38,
  healthStatus: 'critical',
  urgencyLevel: 'high',
  
  executiveSummary: {
    situation: 'Situation Critique mais Opportunite Exceptionnelle : Vous avez un HYPE enorme (couverture Vogue, Timeout, Cosmopolitan) et un concept unique (gastro + clubbing) MAIS votre e-reputation est polarisee (6.7 TheFork vs 5/5 Google) et votre note Google (4.0) est en zone de risque. Chaque jour sans action = clients perdus au profit de concurrents mieux notes.',
    mainChallenges: [
      'CRITIQUE - E-reputation polarisee : Note ambiance 6.7 TheFork (clients calmes decus) vs 5/5 Google (clients festifs ravis). Confusion qui tue votre croissance.',
      'URGENT - Note Google 4.0 en zone rouge : Seuil excellence 4.5. En dessous, Google Maps penalise votre visibilite de 60%. Vous perdez des dizaines de clients par jour.',
      'BLOQUANT - Tomahawk invisible pour les IA : Votre plat signature (1.3kg unique a Paris) est en PDF. ChatGPT ne peut pas vous recommander quand on lui demande "restaurant tomahawk Paris".',
      'PROBLEME - Pas de site dedie : Partage fitz-group.fr. Les algorithmes ne savent pas si vous etes Restaurant ou Night Club. Confusion = perte de visibilite.',
      'OPPORTUNITE - Aucun chat IA : Pas de reponse hors horaires. Concurrent avec chatbot capture vos clients potentiels pendant que vous dormez.'
    ],
    quickWins: [
      '✅ Programme Ambassadeurs (QR code tables festives) : 2 semaines, +40 avis positifs net',
      '✅ Page /tomahawk-13kg + vidéo : 3 semaines, position 12 → 3 estimée',
      '✅ Réponse sémantique avis "bruit" : Immédiat, +20% ratio positif',
      '✅ Séparation pages /restaurant + /savoy-club : 4 semaines, clarté algorithmes'
    ],
    estimatedLostRevenue: '90k€ - 140k€/an',
    potentialGain: '+120k€ - 180k€/an avec corrections urgentes + stratégie 6 mois'
  },
  
  categoryScores: [
    {
      category: 'E-réputation',
      score: 30,
      status: 'critical',
      p0Count: 2,
      p1Count: 2,
      p2Count: 0,
      icon: 'AlertTriangle'
    },
    {
      category: 'GEO & Moteurs IA',
      score: 15,
      status: 'critical',
      p0Count: 3,
      p1Count: 0,
      p2Count: 0,
      icon: 'Globe'
    },
    {
      category: 'SEO Technique',
      score: 35,
      status: 'critical',
      p0Count: 2,
      p1Count: 1,
      p2Count: 0,
      icon: 'Shield'
    },
    {
      category: 'Positionnement',
      score: 40,
      status: 'warning',
      p0Count: 1,
      p1Count: 2,
      p2Count: 0,
      icon: 'Target'
    },
    {
      category: 'Buzz & Freshness',
      score: 75,
      status: 'good',
      p0Count: 0,
      p1Count: 2,
      p2Count: 1,
      icon: 'TrendingUp'
    }
  ],
  
  currentMetrics: {
    seo: {
      organicTraffic: 140,
      positionAverage: 22.3,
      visibilityScore: 58,
      domainAuthority: 35,
      backlinks: 680,
      keywords: 95
    },
    geo: {
      googleRating: 4.0,
      reviewCount: 850,
      citations: 62,
      localPackPresence: '8/95 (8%)',
      gmbCompleteness: 68
    },
    reputation: {
      theForkRating: 8.3,
      serviceScore: 9.3,
      ambianceScore: 6.7,
      foodScore: 8.7
    }
  },
  
  strategicAxes: [
    {
      id: 'axe_hs_1',
      title: '🚨 URGENCE ABSOLUE : Résoudre Polarisation E-réputation',
      description: 'Votre note ambiance TheFork (6.7) est CATASTROPHIQUE alors que Google donne 5/5. Cause: clients calmes déçus par ambiance festive VS clients festifs ravis. Ce "Mismatched Intent" tue votre croissance.',
      currentSituation: 'Note ambiance 6.7 TheFork (47% négatifs "bruit") - Note Google 4.0 (zone risque) - Croissance avis: +15/mois insuffisant',
      targetObjective: 'Note ambiance 8.5 TheFork (alignée avec positionnement) - Note Google 4.5 (seuil excellence) - Croissance: +40 avis/mois',
      priority: 'P0',
      estimatedImpact: 'Note Google 4.0 → 4.5 = +40% visibilité Maps = amélioration découvertes quotidiennes',
      timeline: '3-6 mois stabilisation',
      budget: '5,500€ (QR codes 500€ + incentives 2k€ + monitoring 1k€ + formation 2k€)',
      actions: [
        {
          title: 'Call confirmation J-1 réservations TheFork',
          description: '"Le Savoy est festif avec DJ après 22h. Confirmez-vous votre réservation ?" Si NON → proposer Fontaine Gaillon (calme).',
          isVisible: true
        },
        {
          title: 'QR code avis INTELLIGENT : uniquement tables festives',
          description: 'Distribuer aux tables manifestant joie/danse. Message: "Vous avez aimé l\'ambiance ? Aidez-nous!" + Digestif offert prochaine visite.',
          isVisible: true
        },
        {
          title: 'Template réponse systématique avis "trop bruyant"',
          description: 'Réaffirmer concept: "Le Hollywood Savoy est un lieu vivant et festif CONÇU pour célébrer..."',
          isVisible: true
        },
        {
          title: '[STRATÉGIE REPUTATION MANAGEMENT AVANCÉE]',
          description: 'Segmentation tables zones, workflow qualification clients, A/B testing messaging, partenariats influenceurs... → Programme complet STRATOS',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Note Google', current: '4.0★', target: '4.5★', improvement: '+0.5★ = CRITIQUE' },
        { name: 'Note ambiance TheFork', current: '6.7', target: '8.5', improvement: '+27%' },
        { name: 'Nouveaux avis festifs/mois', current: '~8', target: '40+', improvement: 'x5' },
        { name: 'Visibilité Google Maps', current: '-60% pénalité', target: 'Maximale', improvement: '+60%' }
      ]
    },
    
    {
      id: 'axe_hs_2',
      title: '🤖 URGENT : Tomahawk 1.3kg Invisible pour les IA',
      description: 'Votre plat signature (Tomahawk 1.3kg 58€ - UNIQUE à Paris) est enfermé dans un PDF. Quand un client demande à ChatGPT "Quel restaurant propose un Tomahawk dans le 2ème ?", l\'IA ne peut PAS vous citer.',
      currentSituation: 'Menu déjeuner PDF - Tomahawk non structuré - Recommandations IA: 10% - Position "tomahawk paris": 12',
      targetObjective: 'Menu HTML complet + Schema.org - Recommandations IA: 80% - Position "tomahawk paris": Top 3',
      priority: 'P0',
      estimatedImpact: '+25% visibilité IA = +35 réservations/mois Tomahawk = +2k€ CA/mois',
      timeline: '2-3 semaines digitalisation',
      budget: '5,000€ (dev HTML 2k€ + vidéo découpe 2k€ + photos 1k€)',
      actions: [
        {
          title: 'Création page /tomahawk-13kg-hollywood-savoy-paris',
          description: 'Landing dédiée avec vidéo découpe à table (théâtralisation), photos HD, Schema.org MenuItem complet.',
          isVisible: true
        },
        {
          title: 'Digitalisation menu COMPLET (déjeuner + soir + desserts)',
          description: 'Abandonner PDF. Tous les plats en HTML structuré : Tomahawk 58€, Caviar 85€, Lobster Roll 32€...',
          isVisible: true
        },
        {
          title: 'Test IA systématique',
          description: 'Interroger ChatGPT/Perplexity: "Tomahawk 2ème arrondissement" doit citer Hollywood Savoy.',
          isVisible: true
        },
        {
          title: '[STRATÉGIE GEO COMPLÈTE MASQUÉE]',
          description: 'FAQ optimisée, featured snippets, recherche vocale, monitoring IA, itérations... → Pack Premium STRATOS',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Menu structuré Schema', current: 'Non (PDF)', target: 'Complet HTML', improvement: 'URGENT' },
        { name: 'Recommandations IA Tomahawk', current: '10%', target: '80%', improvement: '+700%' },
        { name: 'Position "tomahawk paris"', current: '12', target: '3', improvement: '-9 positions' },
        { name: 'Trafic plat signature', current: '5%', target: '20%', improvement: '+300%' }
      ]
    },
    
    {
      id: 'axe_hs_3',
      title: '🎭 Clarifier Identité Hybride Restaurant/Club',
      description: 'Google ne sait pas comment vous classer: "Restaurant Français" OU "Night Club" ? Cette confusion vous fait perdre 35% de visibilité sur DEUX segments. Il faut aider les algorithmes à comprendre votre dualité unique.',
      currentSituation: 'Page unique - Classification GMB floue - Cannibalisation keywords - Bounce rate élevé',
      targetObjective: '2 pages distinctes + Double Schema.org (Restaurant + NightClub) - Clarté 90%',
      priority: 'P0',
      estimatedImpact: '+35% visibilité sur segments déjeuner ET soirée = +50 couverts/mois',
      timeline: '4 semaines refonte UX',
      budget: '8,000€ (UX/UI refonte 5k€ + dev 3k€)',
      actions: [
        {
          title: 'Création page /restaurant (focus déjeuner + dîner gastro)',
          description: 'Ambiance chic, plats signatures, photos lumière naturelle. Schema Restaurant.',
          isVisible: true
        },
        {
          title: 'Création page /savoy-club (focus clubbing 23h-2h)',
          description: 'DJ sets, Live Shows, dress code, liste VIP. Schema NightClub.',
          isVisible: true
        },
        {
          title: 'Double balisage GMB: Primary "Restaurant" + Secondary "Night Club"',
          description: 'Horaires distincts : Déj 11h-15h / Dîner 19h-23h / Club 23h-2h.',
          isVisible: true
        },
        {
          title: '[STRATÉGIE UX BIFURQUÉE MASQUÉE]',
          description: 'Personas distincts, content segmenté, campagnes différenciées, A/B testing... → Stratégie complète Premium',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Classification GMB', current: 'Floue', target: 'Double claire', improvement: 'Clarté +150%' },
        { name: 'Trafic déjeuner', current: '25%', target: '40%', improvement: '+60%' },
        { name: 'Trafic soirée clubbing', current: '30%', target: '45%', improvement: '+50%' }
      ]
    },
    
    {
      id: 'axe_hs_4',
      title: '🎵 Capitaliser Hype 2024-2025 & Histoire Caroline Nin',
      description: 'Couverture presse massive (Vogue, Timeout) + storytelling années 80 Caroline Nin = différenciateurs PUISSANTS. Mais risque retombée hype dans 6 mois si non consolidé.',
      currentSituation: 'Articles presse: 8/trim - Page histoire: ABSENTE - Interview Caroline: Non faite - Momentum: Élevé mais temporaire',
      targetObjective: 'Articles: 12/trim - Page /histoire référence - Interview exclusive - Autorité culturelle durable',
      priority: 'P1',
      estimatedImpact: 'Convertir hype temporaire en autorité = maintien trafic post-buzz (+0% vs -40% inaction)',
      timeline: '2-3 mois content + relations presse',
      budget: '13,000€ (interview Caroline 10k€ + content 3k€)',
      actions: [
        {
          title: 'Contact Caroline Nin pour interview exclusive',
          description: 'Elle a débuté au Savoy à 19 ans (1989). Toujours active. Interview "Renaissance du Savoy" = contenu viral unique.',
          isVisible: true
        },
        {
          title: 'Création page /histoire-hollywood-savoy avec timeline 1982-2025',
          description: 'Storytelling émotionnel. Archives photos années 80 (recherche requise).',
          isVisible: true
        },
        {
          title: 'Relations presse continues: 1 article majeur/mois',
          description: 'Maintenir momentum. Cibles: AD, M Le Monde, Jazz Magazine, France Culture.',
          isVisible: true
        },
        {
          title: '[STRATÉGIE CONTENT DURABLE MASQUÉE]',
          description: 'Programme ambassadeurs VIP, collaborations marques luxe, événements générateurs PR, stratégie UGC... → Premium STRATOS',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Autorité culturelle', current: '15 mentions', target: '40+ mentions', improvement: '+167%' },
        { name: 'Trafic post-hype (6 mois)', current: 'Non projeté', target: '+0% vs -40%', improvement: '40% sauvés' },
        { name: 'Partages sociaux /histoire', current: '0', target: '500+', improvement: 'Viral potential' }
      ]
    },
    
    {
      id: 'axe_hs_5',
      title: '🌍 Visibilité Internationale & Canaux Réservation',
      description: 'Vous avez 55% de trafic français vs potentiel 45% international (touristes Paris). Absence TheFork international, OpenTable, Google Reserve = fuites réservations.',
      currentSituation: 'Trafic international: 45% - Plateformes: 1 (TheFork FR) - Chatbot: Aucun',
      targetObjective: 'Trafic international: 60% - 5 plateformes - Chatbot IA multilingue 24/7',
      priority: 'P1',
      estimatedImpact: '+60 réservations internationales/mois = +4.8k€ CA/mois',
      timeline: '2 mois multi-canal',
      budget: 'Inclus Pack Premium',
      actions: [
        {
          title: 'Chatbot IA multilingue (FR/EN/ES/PT)',
          description: 'Réponses instantanées programmation, menu, réservation. Disponible 24/7.',
          isVisible: true
        },
        {
          title: 'Inscription OpenTable (clientèle US)',
          description: 'Touristes américains réservent via OpenTable. Marché non capté actuellement.',
          isVisible: true
        },
        {
          title: '[STRATÉGIE MULTI-CANAL COMPLÈTE]',
          description: 'Google Reserve, Resy, intégrations, dashboard centralisé, analytics attribution... → Pack Premium',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Plateformes actives', current: '1', target: '5', improvement: '+400%' },
        { name: 'Réservations 24/7 (hors horaires)', current: '0', target: '30-50/mois', improvement: 'Nouveau canal' }
      ]
    },
    
    {
      id: 'axe_hs_6',
      title: '📍 Optimisation Fiche Google & Local Pack',
      description: 'Votre fiche GMB est à 68% de complétude. Présence Local Pack: 8/95 keywords (8% seulement). Concurrent Daroco: 45%. Vous perdez 92% opportunités recherches locales.',
      currentSituation: 'GMB 68% - Local Pack 8% - Photos: 55 (vs 150 Daroco) - Posts: irréguliers',
      targetObjective: 'GMB 95% - Local Pack 35% - Photos: 120+ - Posts: 3/semaine réguliers',
      priority: 'P1',
      estimatedImpact: '+100 découvertes spontanées/jour via Maps = +40 réservations/mois',
      timeline: '1-2 mois optimisation',
      budget: '3,500€ (photos 2k€ + optimisation 1.5k€)',
      actions: [
        {
          title: 'Complétion GMB à 95% (attributs, horaires distincts resto/club, services)',
          description: 'Algorithme Google privilégie fiches complètes.',
          isVisible: true
        },
        {
          title: 'Upload 70 photos supplémentaires (objectif 120 total)',
          description: 'Ambiances: déjeuner calme, dîner chic, club festif, plats signatures, décor Art Déco.',
          isVisible: true
        },
        {
          title: '[STRATÉGIE GMB AVANCÉE MASQUÉE]',
          description: 'Posts programmés, Q&A proactive, événements Schema, optimisation recherche vocale... → Inclus Premium',
          isVisible: false
        }
      ],
      kpis: [
        { name: 'Complétude GMB', current: '68%', target: '95%', improvement: '+40%' },
        { name: 'Présence Local Pack', current: '8/95 (8%)', target: '33/95 (35%)', improvement: '+337%' },
        { name: 'Photos GMB', current: '55', target: '120+', improvement: '+118%' }
      ]
    }
  ],
  
  sixMonthPlan: [
    {
      month: 1,
      focus: 'URGENCES : Réputation + GEO',
      keyActions: [
        'Lancer Programme Ambassadeurs (QR codes)',
        'Digitaliser menu Tomahawk + vidéo',
        'Call confirmation TheFork (filtrage clients)',
        'Upload 70 photos GMB'
      ],
      expectedResults: [
        '+20 avis positifs festifs',
        'Menu HTML online',
        'Réduction avis négatifs "bruit" -50%',
        'GMB complétude 85%'
      ],
      investment: '12,000€'
    },
    {
      month: 2,
      focus: 'Clarification Identité + IA',
      keyActions: [
        'Pages /restaurant + /savoy-club online',
        'Schema.org double (Restaurant + NightClub)',
        'Page /tomahawk ranking',
        'Contact Caroline Nin (interview)'
      ],
      expectedResults: [
        'Clarté classification +90%',
        'Position Tomahawk: 12 → 8',
        'Recommandations IA: 10% → 40%',
        '[+ 4 résultats détaillés...]'
      ],
      investment: '15,500€'
    },
    {
      month: 3,
      focus: 'Note Google 4.5 & Autorité',
      keyActions: [
        'Objectif: +40 avis net (atteindre 890 avis)',
        'Page /histoire + timeline online',
        '[3 autres actions clés...]'
      ],
      expectedResults: [
        'Note Google: 4.0 → 4.3',
        '[Détails complets dans accompagnement]'
      ],
      investment: '11,000€'
    },
    {
      month: 4,
      focus: '[STRATÉGIE MOIS 4-6 DÉTAILLÉE]',
      keyActions: [
        'Consolidation réputation',
        'Scale canaux réservation',
        '[Plan complet fourni en accompagnement STRATOS Premium]'
      ],
      expectedResults: [
        'Note Google: 4.3 → 4.5 (seuil excellence)',
        '+45% réservations vs baseline'
      ],
      investment: '[Détails proposition commerciale]'
    },
    {
      month: 5,
      focus: '[OPTIMISATION & SCALE]',
      keyActions: ['[Accès stratégie complète avec STRATOS]'],
      expectedResults: ['[Reporting mensuel détaillé]'],
      investment: '[Inclus Pack]'
    },
    {
      month: 6,
      focus: 'Bilan & Projection An 2',
      keyActions: ['[Programme complet dévoilé lors kick-off]'],
      expectedResults: [
        'Objectif global: Note 4.5★ atteinte',
        '+120-180k€ CA additionnel/an validé'
      ],
      investment: '[Total 6 mois: 75,000€]'
    }
  ],
  
  recommendedPack: {
    name: 'Pack Premium',
    price: '2,000€/mois',
    duration: 'Engagement 6 mois minimum (12 mois recommandé)',
    roi: '180-300% en 12 mois',
    included: [
      '✅ Programme Ambassadeurs e-réputation (QR codes, templates, tracking)',
      '✅ Digitalisation menus + Schema.org complet GEO',
      '✅ Création pages /restaurant + /club + /tomahawk + /histoire',
      '✅ Contact & coordination interview Caroline Nin (si accepte)',
      '✅ Optimisation GMB complète (posts, photos, Q&A)',
      '✅ Chatbot IA multilingue 24/7',
      '✅ Inscription plateformes réservation (OpenTable, etc.)',
      '✅ Monitoring réputation temps réel + alertes',
      '... et 15 autres services → Proposition commerciale détaillée'
    ]
  }
};

// ============================================================================
// Fonctions Utilitaires
// ============================================================================

export const getPreAuditReport = (restaurantId: string): PreAuditReport | null => {
  console.log('getPreAuditReport appelé pour:', restaurantId);
  
  if (restaurantId === 'fontaine-gaillon') {
    console.log('Retour données Fontaine Gaillon');
    return preAuditFontaineGaillon;
  }
  if (restaurantId === 'hollywood-savoy') {
    console.log('Retour données Hollywood Savoy');
    return preAuditHollywoodSavoy;
  }
  
  console.log('⚠️ Pas de pré-audit pour ce restaurant:', restaurantId);
  console.log('Restaurants supportés: fontaine-gaillon, hollywood-savoy');
  return null;
};

export const getOverallScore = (restaurantId: string): number => {
  const report = getPreAuditReport(restaurantId);
  return report?.overallScore || 0;
};

export const getUrgencyLevel = (restaurantId: string): 'high' | 'medium' | 'low' => {
  const report = getPreAuditReport(restaurantId);
  return report?.urgencyLevel || 'low';
};

export const getCriticalIssuesCount = (restaurantId: string): number => {
  const report = getPreAuditReport(restaurantId);
  if (!report) return 0;
  return report.strategicAxes.filter(axe => axe.priority === 'P0').length;
};

export const getEstimatedLostRevenue = (restaurantId: string): string => {
  const report = getPreAuditReport(restaurantId);
  return report?.executiveSummary.estimatedLostRevenue || '0€';
};

export const getPotentialGain = (restaurantId: string): string => {
  const report = getPreAuditReport(restaurantId);
  return report?.executiveSummary.potentialGain || '0€';
};
